import _ from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import { ReferenceComponent, Document } from './types';

export class DocumentClass {
    private document: Document;

    constructor(document: Document) {
        this.document = document;
    }

    public getDocument() {
        return this.document;
    }

    public async lookupReferences(getComp: (cid: string) => Promise<ReferenceComponent | null>) {
        const refs = this.getUnresolvedReferenceIds();
        const refIds = Object.keys(refs);
        for (const refId of refIds) {
            const comp = await getComp(refId);

            if (comp) {
                for (const ref of refs[refId]) {
                    this.insertComponentsFromReference(ref?.id, comp);
                }
            }
        }
    }

    public async removeReferences() {
        const document = this.document;
        const ids = Object.keys(document.widgetInstances);

        for (let i = 0; i < ids.length; i++) {
            const refComponentId = ids[i];
            // const refComponent = document.widgetInstances[refComponentId];
            const widget = document.widgetInstances[refComponentId];
            const isRefWidget = !!widget.refId;

            if (isRefWidget) {
                delete document.widgetInstances[refComponentId];

                if (widget.parentId === widget.refId) {
                    const parent = document.widgetInstances[widget.parentId];

                    if (parent) {
                        const children = parent.contentPlaceHolders.children;
                        const idx = children.indexOf(refComponentId);
                        delete parent.refLoaded;

                        if (idx > -1) {
                            parent.contentPlaceHolders.children.splice(idx, 1);
                        }
                    }
                }
            }
        }
    }

    public getUnresolvedReferenceIds() {
        const undersolvedReferences: Record<
            string,
            { id: string; isPublicRefComponent: boolean | undefined }[]
        > = {};
        const document = this.document;
        const ids = Object.keys(document.widgetInstances);

        for (let i = 0; i < ids.length; i++) {
            const refComponent = document.widgetInstances[ids[i]];

            if (refComponent.widgetId === 'ReferenceComponent' && !refComponent.refLoaded) {
                const props = refComponent.properties as {
                    referenceId: string;
                    isPublicRefComponent: boolean | undefined;
                };

                if (!undersolvedReferences[props.referenceId]) {
                    undersolvedReferences[props.referenceId] = [];
                }

                undersolvedReferences[props.referenceId].push({
                    id: refComponent.id,
                    isPublicRefComponent: props.isPublicRefComponent || undefined,
                });
                // e.g. public/component1
            }
        }

        return undersolvedReferences;
    }

    public insertComponentsFromReference(widgetInstanceId: string, comp: ReferenceComponent) {
        const doc = this.document;
        const refComponent = doc.widgetInstances[widgetInstanceId];

        if (
            refComponent &&
            refComponent.widgetId === 'ReferenceComponent' &&
            !refComponent.refLoaded
        ) {
            // this is a reference component, a reference to an external component
            refComponent.refLoaded = true;

            // reference component widget ids
            const referencedWidgetIds: any = [];

            // tarvers the widgets from the external reference (breadth first)
            // and insert into the component tree of the target document

            // move all children of the 'root'
            if (typeof comp?.widgetInstances === 'object') {
                _.forEach(comp?.widgetInstances?.root?.contentPlaceHolders?.children, (compId) => {
                    // get Ids of children in reference component besides of root document
                    referencedWidgetIds.push(compId);
                });

                // add to the current document
                // do a breadth-first traversal
                while (referencedWidgetIds.length > 0) {
                    const rwId = referencedWidgetIds.shift();
                    const originalComp = cloneDeep(comp);
                    if (rwId && comp?.widgetInstances[rwId]) {
                        const relId = `${widgetInstanceId}/${rwId}`;

                        const widget = {
                            ...originalComp?.widgetInstances[rwId],
                            id: relId,
                            refId: widgetInstanceId,
                        };

                        doc.widgetInstances[relId] = widget;
                        // Update IDs in content placeholders
                        for (const ph of Object.keys(widget?.contentPlaceHolders || {})) {
                            if (widget?.contentPlaceHolders[ph]) {
                                // add children of this node to the queue
                                referencedWidgetIds.push(...widget?.contentPlaceHolders[ph]);

                                // change children Ids in the target document to new, qualified Ids
                                widget.contentPlaceHolders[ph] = widget?.contentPlaceHolders[
                                    ph
                                ].map((x: string) => {
                                    return `${widgetInstanceId}/${x}`;
                                });
                            }
                        }

                        // Update parent and parent's content placeholders
                        const parentId =
                            widget.parentId === 'root'
                                ? widgetInstanceId
                                : `${widgetInstanceId}/${widget.parentId}`;
                        widget.parentId = parentId;

                        const parent = doc.widgetInstances[parentId];
                        const parentPlaceHolders = parent?.contentPlaceHolders?.children || [];
                        // Check if relId is not already present before adding
                        if (!parentPlaceHolders.includes(relId)) {
                            parent.contentPlaceHolders.children = [...parentPlaceHolders, relId];
                        }
                    }
                }
            }
        }
    }
}
